import { I18n } from '@calendly/i18n'
import { ViewSwitcher } from '@calendly/ui/components/view-switcher'
import classNames from 'classnames'

import {
  presetFilterSchedulingSwitchCss,
  schedulingSourceSwitchDesktopCss,
  schedulingSourceSwitchFigmaCss,
} from 'components/meetings/filter_modal/preset_filter_sheet_styles'
import { AllSchedulingMethods, SchedulingMethods } from 'constants/meetings'

import { useMeetingsPagePersistentSettings } from './hooks/use_meetings_page_persistent_settings'

const ALL_MEETINGS_ID = 'all'
const CALENDLY_ONLY_ID = 'calendly'

const isCalendlyOnlyMethods = (methods = []) => methods.length === 1
  && methods.includes(SchedulingMethods.CALENDLY)

/**
 * Segmented scheduling source control from Meetings-Search---Filter (Figma).
 * Uses CUI ViewSwitcher — gray track with a raised white segment for the active option.
 *
 * Pass `value` + `onChange` to drive it as a draft control inside the filter sheet (the
 * sheet's Apply button persists the change). Without them it edits the persistent setting
 * directly (live), as used by the desktop header.
 */
export const MeetingsSchedulingSourceSwitch = ({ fullWidth = false, value, onChange }) => {
  const { schedulingMethods: persistedMethods, handleSchedulingMethodChange } = useMeetingsPagePersistentSettings()
  const isControlled = typeof onChange === 'function'
  const schedulingMethods = isControlled ? (value ?? AllSchedulingMethods) : persistedMethods

  const selectedValue = isCalendlyOnlyMethods(schedulingMethods) ? CALENDLY_ONLY_ID : ALL_MEETINGS_ID

  const options = [
    {
      value: ALL_MEETINGS_ID,
      text: I18n.t('publisher.meetings.persistent_settings.show_meetings.all_meetings_short'),
    },
    {
      value: CALENDLY_ONLY_ID,
      text: I18n.t('publisher.meetings.persistent_settings.show_meetings.calendly_only_short'),
    },
  ]

  const handleChange = ({ target: { value: nextValue } }) => {
    if (nextValue === selectedValue) return

    const nextMethods = nextValue === ALL_MEETINGS_ID
      ? AllSchedulingMethods
      : [SchedulingMethods.CALENDLY]

    if (isControlled) {
      onChange(nextMethods)
    } else {
      handleSchedulingMethodChange(nextMethods)
    }
  }

  return (
    <div
      className={classNames(schedulingSourceSwitchFigmaCss, {
        [presetFilterSchedulingSwitchCss]: fullWidth,
        [schedulingSourceSwitchDesktopCss]: !fullWidth,
      })}
      data-testid="meetings-scheduling-source-switch"
    >
      <ViewSwitcher
        name="meetings-scheduling-source"
        value={selectedValue}
        options={options}
        onChange={handleChange}
      />
    </div>
  )
}
